import { useEffect } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useLocation } from 'react-router-dom';
import { useBannerStore } from '@/state/banner.state';
import { FF_HOLIDAY_PROMO_BANNER } from '@/consts/featureFlags';
import { useCreditsContext } from '@/services/CreditsContext';

export const APP_BANNER_ID = 'app-banner-id';

export interface BannerConfig {
  desktopHeight: number;
  mobileHeight: number;
  featureFlag: string;
}

export const BANNER_CONFIGS: Record<string, BannerConfig> = {
  [APP_BANNER_ID]: {
    desktopHeight: 56,
    mobileHeight: 126,
    featureFlag: FF_HOLIDAY_PROMO_BANNER,
  },
};

export const useBanner = (bannerId: string) => {
  const config = BANNER_CONFIGS[bannerId];
  if (!config) {
    throw new Error(`Banner configuration not found for bannerId: ${bannerId}`);
  }

  // TODO: this is a temporary solution to show the banner only on the pricing plans page and pricing modal.
  // we can improve this by making the conditions dynamic.
  const { isPricingPlansModalOpen } = useCreditsContext();
  const isInPricingPlansPage = useLocation().pathname === '/pricing';
  const shouldDisplayPromoBanner = isInPricingPlansPage || isPricingPlansModalOpen;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isVisible, setIsVisible } = useBannerStore();
  const isFeatureFlagEnabled = useFeatureFlagEnabled(config.featureFlag);
  const bannerHeight = isMobile ? config.mobileHeight : config.desktopHeight;
  const height = isVisible ? bannerHeight : 0;
  useEffect(() => {
    const userDismissed = localStorage.getItem(bannerId) === 'false';
    const shouldShow = (isFeatureFlagEnabled ?? false) && !userDismissed;
    setIsVisible(shouldShow);
  }, [bannerId, isFeatureFlagEnabled, setIsVisible]);

  const closeBanner = () => {
    setIsVisible(false);
    localStorage.setItem(bannerId, 'false');
  };

  const shouldShow = isVisible && shouldDisplayPromoBanner;
  return {
    // State
    isVisible: shouldShow,
    // Computed values
    offset: shouldShow ? height : 0,
    // Actions
    setIsVisible,
    closeBanner,
  };
};
